import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { Bandeau, Carte, Champ, Modale, Squelette, Vide } from '../components/ui';
import { dateFr, montant } from '../lib/format';
import { T } from '../i18n';

/**
 * Supprimer une fiche, définitivement.
 *
 * Hichem l'a demandé. Le logiciel est construit sur l'inverse — on annule, on
 * archive, on corrige, mais on n'efface pas — parce que cette agence ne facture
 * pas tout et que son journal est sa seule défense en cas de litige. Le geste
 * existe donc, mais il laisse sa trace : la fiche entière part au journal avant
 * de disparaître, avec le motif et le nom de qui l'a supprimée.
 */

type Fiche = Record<string, unknown> & { _id: string };

const TYPES = (): { cle: string; libelle: string; url: string; decrire: (d: Fiche) => string; aide?: string }[] => [
  {
    cle: 'vehicule', libelle: T.suppression.types.vehicule, url: '/vehicules',
    decrire: (d) => `${d.marque} ${d.modele} · ${d.immatriculation}`,
    aide: T.suppression.aideVehicule,
  },
  { cle: 'client', libelle: T.suppression.types.client, url: '/clients',
    decrire: (d) => String(d.raisonSociale || `${d.prenom ?? ''} ${d.nom ?? ''}`.trim()) },
  { cle: 'location', libelle: T.suppression.types.location, url: '/reservations',
    decrire: (d) => `${d.numero} · ${dateFr(String(d.dateDebut))} → ${dateFr(String(d.dateFin))} · ${montant(Number(d.totalPrevuMinor ?? 0))}`,
    aide: T.suppression.aideLocation },
  { cle: 'contrat', libelle: T.suppression.types.contrat, url: '/contrats',
    decrire: (d) => `${d.numero} · ${montant(Number(d.totalGeneralMinor ?? 0))}` },
  { cle: 'facture', libelle: T.suppression.types.facture, url: '/factures',
    decrire: (d) => `${d.numero} · ${montant(Number(d.totalMinor ?? 0))}`,
    aide: T.suppression.aideFacture },
  { cle: 'caution', libelle: T.suppression.types.caution, url: '/cautions',
    decrire: (d) => `${d.dechargeRemiseNumero} · ${montant(Number(d.montantMinor ?? 0))}` },
  { cle: 'entretien', libelle: T.suppression.types.entretien, url: '/operations/entretiens',
    decrire: (d) => `${d.type} · ${dateFr(String(d.date))} · ${montant(Number(d.coutMinor ?? 0))}` },
  { cle: 'amende', libelle: T.suppression.types.amende, url: '/operations/amendes',
    decrire: (d) => `${d.numeroPV ?? T.suppression.contravention} · ${montant(Number(d.montantMinor ?? 0))}` },
  { cle: 'sinistre', libelle: T.suppression.types.sinistre, url: '/operations/sinistres',
    decrire: (d) => `${dateFr(String(d.date ?? d.createdAt))} · ${String(d.description ?? '').slice(0, 60)}` },
  { cle: 'assurance', libelle: T.suppression.types.assurance, url: '/operations/assurances',
    decrire: (d) => `${d.numeroPolice ?? T.suppression.police} · ${d.assureur ?? ''}` },
];

export default function Suppression() {
  const types = TYPES();
  const [type, setType] = useState(types[0]!);
  const [liste, setListe] = useState<Fiche[] | null>(null);
  const [recherche, setRecherche] = useState('');
  const [choisie, setChoisie] = useState<Fiche | null>(null);
  const [motif, setMotif] = useState('');
  const [msg, setMsg] = useState('');
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => {
    setListe(null);
    api.get<Fiche[] | { donnees?: Fiche[] }>(type.url)
      .then((d) => setListe(Array.isArray(d) ? d : d.donnees ?? []))
      .catch(() => setListe([]));
  }, [type]);
  useEffect(charger, [charger]);

  async function supprimer() {
    if (!choisie) return;
    setErreur('');
    try {
      const r = await api.post<{ libelle: string }>(`/suppression/${type.cle}/${choisie._id}`, { motif });
      setMsg(T.suppression.supprimee(type.libelle, r.libelle));
      setChoisie(null); setMotif(''); charger();
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.suppression.echec); }
  }

  const visibles = (liste ?? []).filter((d) =>
    type.decrire(d).toLowerCase().includes(recherche.trim().toLowerCase()));

  const ts = T.suppression;
  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{ts.titre}</h1>
          <p>{ts.sousTitre}</p>
        </div>
      </div>

      {msg && <div style={{ marginBottom: 12 }}><Bandeau type="ok">{msg}</Bandeau></div>}

      <Bandeau type="warn" titre={ts.avertTitre}>
        {ts.avertTexte}
      </Bandeau>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '14px 0' }}>
        {types.map((t) => (
          <button key={t.cle} className={`btn btn-s ${t.cle === type.cle ? 'btn-p' : ''}`}
                  onClick={() => { setType(t); setRecherche(''); setChoisie(null); }}>
            {t.libelle}
          </button>
        ))}
      </div>

      {type.aide && <div style={{ marginBottom: 12 }}><Bandeau type="info">{type.aide}</Bandeau></div>}

      <Carte titre={ts.fiches(type.libelle, visibles.length)} pad={false} action={
        <input placeholder={ts.chercher} value={recherche} onChange={(e) => setRecherche(e.target.value)} />
      }>
        {liste === null ? <div className="carte-p"><Squelette lignes={4} /></div>
          : visibles.length === 0 ? <div className="carte-p"><Vide titre={ts.rien} /></div> : (
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <tbody>
                  {visibles.slice(0, 200).map((d) => (
                    <tr key={d._id}>
                      <td>{type.decrire(d)}</td>
                      <td className="droite">
                        <button className="btn btn-s" onClick={() => { setChoisie(d); setErreur(''); }}>{ts.supprimer}</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Carte>

      {choisie && (
        <Modale titre={ts.definitivement} onFermer={() => setChoisie(null)}>
          <Bandeau type="ko" titre={type.decrire(choisie)}>
            {ts.modaleTexte}
          </Bandeau>
          <div style={{ marginTop: 14 }}>
            <Champ label={ts.motif} aide={ts.motifAide}>
              <textarea value={motif} onChange={(e) => setMotif(e.target.value)}
                        placeholder={ts.motifExemple} />
            </Champ>
          </div>
          {erreur && <div style={{ marginTop: 10 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setChoisie(null)}>{ts.annuler}</button>
            <button className="btn btn-d" disabled={motif.trim().length < 5} onClick={() => void supprimer()}>
              {ts.definitivement}
            </button>
          </div>
        </Modale>
      )}
    </>
  );
}
